import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import ArrowRightRoundedIcon from "@mui/icons-material/ArrowRightRounded";
import {
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import type { BalanceSheetCell, BalanceSheetDataResponse } from "../../../../../../api/types/svTypes.ts";
import type { BalanceSheetExpandMode } from "../utils/tree.ts";
import {
  buildBalanceSheetTree,
  getBalanceSheetRowVariant,
  getExpandedRowIdsByMode,
  getVisibleBalanceSheetRows,
  resolveExpandModeFromState,
} from "../utils/tree.ts";

interface Props {
  report: BalanceSheetDataResponse;
  expandMode: BalanceSheetExpandMode | null;
  onExpandModeResolved: (mode: BalanceSheetExpandMode | null) => void;
  onDrillDown?: (cell: BalanceSheetCell) => void;
}

const FIRST_COLUMN_WIDTH = 440;
const VALUE_COLUMN_MIN_WIDTH = 120;
const STICKY_HEADER_Z_INDEX = 4;
const STICKY_BODY_Z_INDEX = 2;

function splitHeaderCaption(accountNo: string, caption: string): string {
  const prefix = `${accountNo} `;
  return caption.startsWith(prefix) ? caption.substring(prefix.length) : caption;
}

const BalanceSheetTable = ({ report, expandMode, onExpandModeResolved, onDrillDown }: Props) => {
  const tree = useMemo(() => buildBalanceSheetTree(report.data), [report.data]);
  const tableMinWidth = useMemo(
    () => FIRST_COLUMN_WIDTH + report.columns.length * VALUE_COLUMN_MIN_WIDTH,
    [report.columns.length]
  );
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() =>
    getExpandedRowIdsByMode(tree, expandMode ?? "expanded")
  );

  useEffect(() => {
    if (!expandMode) {
      return;
    }

    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setExpandedIds(getExpandedRowIdsByMode(tree, expandMode));
  }, [tree, expandMode]);

  const visibleRows = useMemo(() => getVisibleBalanceSheetRows(tree, expandedIds), [tree, expandedIds]);

  const onToggleRow = (rowId: string) => {
    const next = new Set(expandedIds);
    if (next.has(rowId)) {
      next.delete(rowId);
    } else {
      next.add(rowId);
    }
    setExpandedIds(next);
    onExpandModeResolved(resolveExpandModeFromState(tree, next));
  };

  return (
    <ScrollableFlexContainer>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 1.5, maxHeight: "calc(100vh - 200px)", position: "relative", overflowX: "auto" }}
      >
        <Table stickyHeader size="small" sx={{ tableLayout: "fixed", width: "100%", minWidth: tableMinWidth }}>
          <TableHead>
            <TableRow sx={{ height: 36 }}>
              <TableCell
                sx={{
                  width: FIRST_COLUMN_WIDTH,
                  minWidth: FIRST_COLUMN_WIDTH,
                  position: "sticky",
                  left: 0,
                  zIndex: STICKY_HEADER_Z_INDEX,
                  borderRight: 1,
                  borderColor: "divider",
                }}
              />
              {report.columns.map((column) => (
                <TableCell
                  key={column.guid}
                  align="right"
                  sx={{ minWidth: VALUE_COLUMN_MIN_WIDTH, whiteSpace: "nowrap" }}
                >
                  {column.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleRows.map(({ node, hasChildren, isExpanded }) => {
              const { row } = node;
              const variant = getBalanceSheetRowVariant(row.header.accountType);

              const rowBackground = variant === "total" ? "background.grey" : "background.paper";
              const rowHoverBackground = "action.hover";

              const fontWeight = variant === "item" ? 400 : 500;
              const caption = splitHeaderCaption(row.header.accountNo, row.header.caption);

              return (
                <TableRow
                  key={node.id}
                  hover
                  sx={{
                    height: 36,
                    "& > .MuiTableCell-root": { backgroundColor: rowBackground },
                    "&:hover > .MuiTableCell-root": { backgroundColor: rowHoverBackground },
                  }}
                >
                  <TableCell
                    sx={{
                      width: FIRST_COLUMN_WIDTH,
                      minWidth: FIRST_COLUMN_WIDTH,
                      position: "sticky",
                      left: 0,
                      zIndex: STICKY_BODY_Z_INDEX,
                      borderRight: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{ alignItems: "center", pl: row.header.indentationLevel * 2 }}
                    >
                      {hasChildren ? (
                        <IconButton size="small" sx={{ py: 0 }} onClick={() => onToggleRow(node.id)}>
                          {isExpanded ? (
                            <ArrowDropDownRoundedIcon fontSize="small" />
                          ) : (
                            <ArrowRightRoundedIcon fontSize="small" />
                          )}
                        </IconButton>
                      ) : (
                        <Stack sx={{ width: 28 }} />
                      )}
                      <Typography variant="body1" color="textSecondary" sx={{ minWidth: 60, fontWeight }}>
                        {row.header.accountNo}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight }}>
                        {caption}
                      </Typography>
                    </Stack>
                  </TableCell>
                  {report.columns.map((column) => {
                    const cell = row.data[column.guid];
                    const canDrillDown = !!cell?.drillDownId;
                    return (
                      <TableCell
                        key={`${node.id}_${column.guid}`}
                        align="right"
                        sx={{ minWidth: VALUE_COLUMN_MIN_WIDTH, whiteSpace: "nowrap" }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight,
                            whiteSpace: "nowrap",
                            ...(canDrillDown ? { cursor: "pointer", "&:hover": { textDecoration: "underline" } } : {}),
                          }}
                          onClick={() => canDrillDown && cell && onDrillDown?.(cell)}
                        >
                          {cell?.formattedValue ?? ""}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </ScrollableFlexContainer>
  );
};

export default BalanceSheetTable;
